import React, { useState, useEffect } from "react";
import {
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  UserCircleIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  BriefcaseIcon,
  ClockIcon,
  PencilIcon,
  PowerIcon,
  HomeIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  AdminDownloadProfilePdfService,
  ToggleEmployeeStatusService,
  UpdateEmployeeService,
  GetEmployeeByCodeService,
  DownloadAllfilesService,
  DeleteEmployeeService,
  GetOptionsService,
} from "../services/ApiService";

const EmployeeCard = ({ employee, isOpen, onToggle, onDownload, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState(
    employee.is_account_active ?? false,
  );
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [fullEmployeeData, setFullEmployeeData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);

  const openModal = async () => {
    setIsModalOpen(true);

    // FETCH ALWAYS (Remove the employee.registrationComplete check)
    if (!fullEmployeeData) {
      setLoadingDetails(true);
      try {
        const data = await GetEmployeeByCodeService(employee.employeeId);
        setFullEmployeeData(data);
        setEditedData(data);

        await fetchDepartments();
        await fetchPositions();
      } catch (error) {
        console.error("Error fetching details:", error);
        alert("Failed to load details.");
      } finally {
        setLoadingDetails(false);
      }
    } else {
        // Just refresh dropdowns if data exists
        await fetchDepartments();
        await fetchPositions();
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    setActiveStatus(employee.is_account_active ?? false);
  }, [employee]);

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await GetOptionsService("DEPARTMENT");
      setDepartments(response || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      setErrors((prev) => ({
        ...prev,
        submit: "Failed to load departments. Please refresh the page.",
      }));
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchPositions = async () => {
    try {
      setLoadingPositions(true);
      const response = await GetOptionsService("POSITION");
      setPositions(response || []);
    } catch (error) {
      console.error("Failed to fetch positions:", error);
      setErrors((prev) => ({
        ...prev,
        position: "Failed to load positions.",
      }));
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  const handleCreateDepartment = (newDept) => {
    // Add to local state immediately
    setDepartments((prev) => [...prev, newDept]);
    // Set as selected value
    setEditedData((prev) => ({ ...prev, department: newDept, position: "" }));
    console.log("✅ New department created:", newDept);
  };

  const handleCreatePosition = (newPos) => {
    // Add to local state immediately
    setPositions((prev) => [...prev, newPos]);
    // Set as selected value
    setEditedData((prev) => ({ ...prev, position: newPos }));
    console.log("✅ New position created:", newPos);
  };

  // ✅ Check if field can be edited based on onboarding status
  const canEditField = (fieldName) => {
    // If registration is complete, EVERYTHING is editable
    if (employee.registrationComplete) {
        return true;
    }

    // If registration is FALSE, only THESE fields are editable
    const allowedFields = [
        "employee_code", // or "username" depending on your API key
        "username",      // including both common keys just in case
        "first_name",
        "middle_name",
        "last_name",
        "email",
        "phone",
        "mobile_number", // including both keys
        "phone_code",
        "department",
        "position",
        "date_of_joining"
    ];
    return allowedFields.includes(fieldName);
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleToggleStatus = async () => {
    if (isTogglingStatus) return;

    try {
      setIsTogglingStatus(true);
      const newStatus = !activeStatus;

      await ToggleEmployeeStatusService(employee.employeeId, newStatus);

      setActiveStatus(newStatus);

      if (onUpdate) {
        onUpdate(employee.id, { is_account_active: newStatus });
      }
    } catch (error) {
      console.error("Error toggling employee status:", error);
      ("Failed to toggle status. Please try again.");
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSaveEdits = async () => {
    try {
      // Create base payload with all data
      const updatePayload = {
        // Personal details - only include if editable
        ...(canEditField("first_name") && {
          first_name: editedData.first_name,
        }),
        ...(canEditField("middle_name") && {
          middle_name: editedData.middle_name,
        }),
        ...(canEditField("last_name") && { last_name: editedData.last_name }),
        ...(canEditField("mobile_number") && {
          mobile_number: editedData.mobile_number,
        }),
        ...(canEditField("email") && { email: editedData.email }),
        ...(canEditField("date_of_birth") && {
          date_of_birth: editedData.date_of_birth,
        }),
        ...(canEditField("father_name") && {
          father_name: editedData.father_name,
        }),
        ...(canEditField("mother_name") && {
          mother_name: editedData.mother_name,
        }),
        ...(canEditField("gender") && { gender: editedData.gender }),
        ...(canEditField("blood_group") && {
          blood_group: editedData.blood_group,
        }),
        ...(canEditField("permanent_address") && {
          permanent_address: editedData.permanent_address,
        }),
        ...(canEditField("current_address") && {
          current_address: editedData.current_address,
        }),

        // Employment details - only include if editable
        ...(canEditField("department") && {
          department: editedData.department,
        }),
        ...(canEditField("position") && { position: editedData.position }),
        ...(canEditField("date_of_joining") && {
          date_of_joining: editedData.date_of_joining,
        }),

        // KYC details - only include if registration is complete
        ...(employee.registrationComplete && {
          aadhaar_number: editedData.aadhaar_number,
          pan_number: editedData.pan_number,
          bank_account_no: editedData.bank_account_no,
          ifsc_code: editedData.ifsc_code,
          passport_number: editedData.passport_number,

          // Education - only if registration is complete
          education:
            editedData.education_details?.map((edu) => ({
              level: edu.level,
              other_level_text:
                edu.level === "Other" ? edu.other_level_text : null,
              institution_name: edu.institution_name,
              specialization: edu.specialization || null,
              year_of_passing: edu.year_of_passing,
              percentage_marks: edu.percentage_marks,
            })) || [],

          // Work experience - only if registration is complete
          work_experience:
            editedData.work_experience?.map((work) => ({
              company_name: work.company_name,
              role: work.role,
              start_date: work.start_date,
              end_date: work.end_date || null,
              last_salary: work.last_salary || null,
            })) || [],
        }),
      };

      // Remove undefined/null values
      const cleanPayload = {};
      for (const [key, value] of Object.entries(updatePayload)) {
        if (value !== undefined && value !== null) {
          cleanPayload[key] = value;
        } else if (key === "education" || key === "work_experience") {
          cleanPayload[key] = value || [];
        }
      }

      console.log("Sending update payload:", cleanPayload);
      await UpdateEmployeeService(employee.employeeId, cleanPayload);

      // Re-fetch updated data
      const updatedData = await GetEmployeeByCodeService(employee.employeeId);
      setFullEmployeeData(updatedData);
      setEditedData(updatedData);

      setIsEditing(false);
      alert("✅ Employee details updated successfully!");

      if (onUpdate) {
        onUpdate(employee.id, updatedData);
      }
    } catch (error) {
      console.error("Error updating employee:", error);
      alert(
        `❌ Failed to update employee: ${error.message || "Unknown error"}`,
      );
    }
  };

  // FIXED: Remove the window.confirm from here
  const handleDeleteEmployee = async () => {
    try {
      setIsDeleting(true);
      // This is where the CORS error happens - backend needs to allow localhost:8080
      await DeleteEmployeeService(employee.employeeId);
      `${employee.name} has been deleted successfully.`;
      closeModal();
      // Refresh the employee list
      window.location.reload();
    } catch (error) {
      console.error("Error deleting employee:", error);
      // Show more detailed error message
      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("CORS")
      ) {
        ("Failed to delete employee due to CORS issue. Please check backend configuration or contact your administrator.");
      } else {
        ("Failed to delete employee. Please try again.");
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Check if field is editable
    if (!canEditField(field)) {
      return;
    }

    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEducation = [...editedData.education_details];
    updatedEducation[index][field] = value;
    setEditedData((prev) => ({
      ...prev,
      education_details: updatedEducation,
    }));
  };

  const handleWorkExperienceChange = (index, field, value) => {
    const updatedWork = [...editedData.work_experience];
    updatedWork[index][field] = value;
    setEditedData((prev) => ({
      ...prev,
      work_experience: updatedWork,
    }));
  };

  // Delete Confirmation Dialog Component
  const DeleteConfirmationDialog = () => {
    if (!showDeleteConfirm) return null;

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
          onClick={() => setShowDeleteConfirm(false)}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95 duration-200 border border-red-100">
          <div className="text-center">
            {/* Icon */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Employee
            </h3>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{employee.name}</span>? This
              action cannot be undone and all data will be permanently removed.
            </p>

            {/* Employee Info */}
            <div className="bg-red-50 rounded-lg p-4 mb-6 border border-red-200">
              <div className="flex items-center justify-center space-x-2">
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {employee.name}
                  </p>
                  <p className="text-xs text-gray-600">
                    {employee.role} • {employee.department}
                  </p>
                  <p className="text-xs font-mono text-gray-500">
                    {employee.employeeId}
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors duration-200"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEmployee} // This now calls the API directly
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg transition-all duration-200 relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    </div>
                    <span className="opacity-0">Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4 mr-2 inline" />
                    Delete Employee
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EditableDropdownField = ({
    label,
    value,
    isEditing,
    options,
    loading,
    onCreateNew,
    onChange,
    canEdit,
  }) => {
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [newValue, setNewValue] = useState("");

    const handleCreateClick = () => {
      if (newValue.trim()) {
        onCreateNew(newValue.trim());
        setNewValue("");
        setIsCreatingNew(false);
      }
    };

    if (!isEditing || !canEdit) {
      return (
        <div>
          <span className="text-xs text-gray-500 block mb-1">{label}</span>
          <p className="font-medium text-gray-900">{value || "N/A"}</p>
        </div>
      );
    }

    return (
      <div>
        <span className="text-xs text-gray-500 block mb-1">{label}</span>
        {loading ? (
          <div className="px-3 py-2 text-sm border border-gray-300 rounded-lg">
            Loading...
          </div>
        ) : (
          <>
            <select
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 transition-colors duration-200"
            >
              <option value="">Select {label}</option>
              {options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>

            {/* Add new option */}
            {!isCreatingNew ? (
              <button
                type="button"
                onClick={() => setIsCreatingNew(true)}
                className="mt-2 text-xs text-emerald-600 hover:text-emerald-800 flex items-center"
              >
                <PencilIcon className="h-3 w-3 mr-1" />
                Add new {label.toLowerCase()}
              </button>
            ) : (
              <div className="mt-2 flex items-center space-x-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder={`Enter new ${label.toLowerCase()}`}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  type="button"
                  onClick={handleCreateClick}
                  className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingNew(false);
                    setNewValue("");
                  }}
                  className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Employee Card */}
      <div className="relative group">
        {/* Green Glow Border on Hover */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-400 via-green-500 to-teal-400 rounded-xl blur opacity-0 group-hover:opacity-20 transition duration-300 group-hover:duration-500"></div>

        <div className="relative bg-white rounded-xl border border-emerald-200 overflow-hidden hover:border-emerald-300 hover:shadow-lg transition-all duration-300 group-hover:shadow-emerald-100 group-hover:-translate-y-1">
          <div className="relative">
            <div className="h-48 overflow-hidden">
              <img
                src={employee.photo}
                alt={employee.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    employee.name,
                  )}&background=10b981&color=fff&size=200`;
                }}
                className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            <div className="absolute top-4 right-4">
              {employee.registrationComplete ? (
                <div className="flex items-center bg-gradient-to-r from-emerald-500 to-green-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Complete
                </div>
              ) : (
                <div className="flex items-center bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                  <XCircleIcon className="h-4 w-4 mr-1" />
                  Incomplete
                </div>
              )}
            </div>

            <div className="absolute bottom-4 left-4">
              <span className="bg-white/95 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-medium shadow-sm border border-emerald-100">
                {employee.department}
              </span>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3
                  className="text-lg font-bold text-gray-900 truncate flex-1"
                  title={employee.name}
                >
                  {employee.name}
                </h3>

                <button
                  onClick={handleToggleStatus}
                  disabled={isTogglingStatus}
                  className={`flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm transition-all duration-200 group relative ${
                    activeStatus
                      ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 hover:from-emerald-200 hover:to-green-200"
                      : "bg-gradient-to-r from-red-100 to-pink-100 text-red-700 hover:from-red-200 hover:to-pink-200"
                  } ${isTogglingStatus ? "opacity-70 cursor-not-allowed" : ""}`}
                  title={
                    activeStatus ? "Click to deactivate" : "Click to activate"
                  }
                >
                  <PowerIcon className="h-3 w-3 mr-1" />
                  {activeStatus ? "Active" : "Inactive"}
                  {isTogglingStatus && (
                    <span className="ml-1 animate-spin">⟳</span>
                  )}
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-1">{employee.role}</p>
              <div className="flex items-center mt-2">
                <UserCircleIcon className="h-4 w-4 text-emerald-500 mr-1 flex-shrink-0" />
                <span
                  className="text-sm font-mono text-gray-700 truncate"
                  title={employee.employeeId}
                >
                  {employee.employeeId}
                </span>
              </div>
            </div>

            <div className="flex items-center text-sm text-gray-600 mb-6">
              <CalendarIcon className="h-4 w-4 mr-2 text-emerald-400 flex-shrink-0" />
              <span>
                Hired: {new Date(employee.hireDate).toLocaleDateString()}
              </span>
            </div>

            {/* View Details + Delete Button Container */}
            <div className="flex items-center space-x-2">
              {/* View Details Button */}
              <button
                onClick={openModal}
                className="flex-1 flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-xl transition-all duration-200 overflow-hidden group/btn relative"
                aria-label={`View full details for ${employee.name}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300"></div>
                <EyeIcon className="h-4 w-4 mr-2 relative z-10" />
                <span className="relative z-10">View Details</span>
              </button>

              {/* Delete Button */}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl transition-all duration-200 overflow-hidden group/delete relative"
                aria-label={`Delete ${employee.name}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 opacity-0 group-hover/delete:opacity-20 transition-opacity duration-300"></div>
                <TrashIcon className="h-4 w-4 relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog />

      {/* Employee Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={closeModal}
            aria-hidden="true"
          />

          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-300 border border-emerald-100"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-8 pt-8 pb-6 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-2xl overflow-hidden ring-4 ring-white shadow-lg border-2 border-emerald-200">
                        <img
                          src={employee.photo}
                          alt={employee.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              employee.name,
                            )}&background=10b981&color=fff&size=200`;
                          }}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div
                        className={`absolute -bottom-2 -right-2 w-10 h-10 rounded-full border-4 border-white ${
                          employee.registrationComplete
                            ? "bg-gradient-to-r from-emerald-500 to-green-600"
                            : "bg-gradient-to-r from-amber-500 to-orange-500"
                        }`}
                      >
                        {employee.registrationComplete ? (
                          <CheckCircleIcon className="w-full h-full p-1.5 text-white" />
                        ) : (
                          <ClockIcon className="w-full h-full p-1.5 text-white" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-gray-900">
                        {employee.name}
                      </h2>
                      <p className="text-lg text-gray-600 mt-1">
                        {employee.role}
                      </p>
                      <div className="flex items-center mt-3 space-x-3">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200">
                          <BuildingOfficeIcon className="w-4 h-4 mr-1.5" />
                          {employee.department}
                        </span>

                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                            activeStatus
                              ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200"
                              : "bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200"
                          }`}
                        >
                          <PowerIcon className="w-4 h-4 mr-1" />
                          {activeStatus ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Edit Button - Only show if onboarding complete */}
                    {fullEmployeeData && (
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                          isEditing
                            ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white"
                            : "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300"
                        }`}
                      >
                        <PencilIcon className="w-4 h-4 mr-1" />
                        {isEditing ? "Cancel Edit" : "Edit Details"}
                      </button>
                    )}

                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-green-50 rounded-xl transition-all duration-200 border border-transparent hover:border-emerald-200 group"
                      aria-label="Close modal"
                    >
                      <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-emerald-600" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content - Scrollable */}
             <div className="overflow-y-auto max-h-[calc(90vh-250px)] p-8">
                {loadingDetails ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">
                      Loading employee details...
                    </p>
                  </div>
                ) : fullEmployeeData && editedData ? (
                  /* ✅ CHANGE: Logic updated to show form even if !registrationComplete */
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <InfoSection
                      title="Personal Information"
                      icon={<UserCircleIcon className="w-5 h-5" />}
                    >
                      <InfoGrid>
                        <EditableField
                          label="First Name"
                          value={editedData.first_name}
                          isEditing={isEditing}
                          onChange={(value) =>
                            handleInputChange("first_name", value)
                          }
                        />
                        <EditableField
                          label="Middle Name"
                          value={editedData.middle_name}
                          isEditing={isEditing}
                          onChange={(value) =>
                            handleInputChange("middle_name", value)
                          }
                        />
                        <EditableField
                          label="Last Name"
                          value={editedData.last_name}
                          isEditing={isEditing}
                          onChange={(value) =>
                            handleInputChange("last_name", value)
                          }
                        />
                        {/* ✅ Only show extended personal info if Registered */}
                        {employee.registrationComplete && (
                          <>
                            <EditableField
                              label="Date of Birth"
                              value={editedData.date_of_birth}
                              isEditing={isEditing}
                              type="date"
                              onChange={(value) =>
                                handleInputChange("date_of_birth", value)
                              }
                            />
                            <EditableField
                              label="Gender"
                              value={editedData.gender}
                              isEditing={isEditing}
                              onChange={(value) =>
                                handleInputChange("gender", value)
                              }
                            />
                            <EditableField
                              label="Blood Group"
                              value={editedData.blood_group}
                              isEditing={isEditing}
                              onChange={(value) =>
                                handleInputChange("blood_group", value)
                              }
                            />
                            <EditableField
                              label="Father's Name"
                              value={editedData.father_name}
                              isEditing={isEditing}
                              onChange={(value) =>
                                handleInputChange("father_name", value)
                              }
                            />
                            <EditableField
                              label="Mother's Name"
                              value={editedData.mother_name}
                              isEditing={isEditing}
                              onChange={(value) =>
                                handleInputChange("mother_name", value)
                              }
                            />
                          </>
                        )}
                      </InfoGrid>
                    </InfoSection>

                    {/* Contact Information */}
                    <InfoSection
                      title="Contact Information"
                      icon={<PhoneIcon className="w-5 h-5" />}
                    >
                      <InfoGrid>
                        <EditableField
                          label="Email"
                          value={editedData.email}
                          isEditing={isEditing}
                          type="email"
                          onChange={(value) =>
                            handleInputChange("email", value)
                          }
                        />
                        <EditableField
                          label="Mobile Number"
                          value={editedData.mobile_number}
                          isEditing={isEditing}
                          onChange={(value) =>
                            handleInputChange("mobile_number", value)
                          }
                        />
                        {/* ✅ Only show Addresses if Registered */}
                        {employee.registrationComplete && (
                          <>
                            <EditableField
                              label="Permanent Address"
                              value={editedData.permanent_address}
                              isEditing={isEditing}
                              fullWidth
                              onChange={(value) =>
                                handleInputChange("permanent_address", value)
                              }
                            />
                            <EditableField
                              label="Current Address"
                              value={editedData.current_address}
                              isEditing={isEditing}
                              fullWidth
                              onChange={(value) =>
                                handleInputChange("current_address", value)
                              }
                            />
                          </>
                        )}
                      </InfoGrid>
                    </InfoSection>

                    {/* Employment Details */}
                    <InfoSection
                      title="Employment Details"
                      icon={<BriefcaseIcon className="w-5 h-5" />}
                    >
                      <InfoGrid>
                        {/* ✅ Allow editing ID if Not Registered */}
                        {!employee.registrationComplete && isEditing ? (
                           <EditableField
                             label="Employee ID"
                             value={editedData.username}
                             isEditing={isEditing}
                             onChange={(value) =>
                               handleInputChange("username", value)
                             }
                           />
                        ) : (
                          <InfoItem
                            label="Employee ID"
                            value={fullEmployeeData.username}
                            mono
                          />
                        )}

                        <EditableDropdownField
                          label="Department"
                          value={editedData.department}
                          isEditing={isEditing}
                          options={departments}
                          loading={loadingDepartments}
                          onCreateNew={handleCreateDepartment}
                          onChange={(value) =>
                            handleInputChange("department", value)
                          }
                          canEdit={canEditField("department")}
                        />
                        <EditableDropdownField
                          label="Position"
                          value={editedData.position}
                          isEditing={isEditing}
                          options={positions}
                          loading={loadingPositions}
                          onCreateNew={handleCreatePosition}
                          onChange={(value) =>
                            handleInputChange("position", value)
                          }
                          canEdit={canEditField("position")}
                        />
                        <EditableField
                          label="Date of Joining"
                          value={editedData.date_of_joining}
                          isEditing={isEditing}
                          type="date"
                          onChange={(value) =>
                            handleInputChange("date_of_joining", value)
                          }
                        />
                        
                        {employee.registrationComplete && (
                            <InfoItem
                              label="Tenure"
                              value={fullEmployeeData.tenure}
                            />
                        )}
                        <InfoItem
                          label="Account Status"
                          value={
                            fullEmployeeData.is_account_active
                              ? "Active"
                              : "Inactive"
                          }
                        />
                      </InfoGrid>
                    </InfoSection>

                    {/* ✅ Hide KYC if Not Registered */}
                    {employee.registrationComplete && (
                      <InfoSection
                        title="KYC Details"
                        icon={<IdentificationIcon className="w-5 h-5" />}
                      >
                        <InfoGrid>
                          <EditableField
                            label="Aadhaar Number"
                            value={editedData.aadhaar_number}
                            isEditing={isEditing}
                            mono
                            onChange={(value) =>
                              handleInputChange("aadhaar_number", value)
                            }
                          />
                          <EditableField
                            label="PAN Number"
                            value={editedData.pan_number}
                            isEditing={isEditing}
                            mono
                            onChange={(value) =>
                              handleInputChange("pan_number", value)
                            }
                          />
                          <EditableField
                            label="Bank Account No"
                            value={editedData.bank_account_no}
                            isEditing={isEditing}
                            mono
                            onChange={(value) =>
                              handleInputChange("bank_account_no", value)
                            }
                          />
                          <EditableField
                            label="IFSC Code"
                            value={editedData.ifsc_code}
                            isEditing={isEditing}
                            mono
                            onChange={(value) =>
                              handleInputChange("ifsc_code", value)
                            }
                          />
                          <EditableField
                            label="Passport Number"
                            value={editedData.passport_number || "N/A"}
                            isEditing={isEditing}
                            mono
                            onChange={(value) =>
                              handleInputChange("passport_number", value)
                            }
                          />
                        </InfoGrid>
                      </InfoSection>
                    )}

                    {/* ✅ Hide Education if Not Registered */}
                    {employee.registrationComplete &&
                      fullEmployeeData.education_details &&
                      fullEmployeeData.education_details.length > 0 && (
                        <InfoSection
                          title="Education Details"
                          icon={<AcademicCapIcon className="w-5 h-5" />}
                        >
                          <div className="space-y-4">
                            {editedData.education_details.map((edu, index) => (
                              <div
                                key={edu.id || index}
                                className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200 hover:border-emerald-300 transition-colors duration-200"
                              >
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <EditableField
                                    label="Qualification"
                                    value={
                                      edu.level === "Other"
                                        ? edu.other_level_text
                                        : edu.level
                                    }
                                    isEditing={isEditing}
                                    onChange={(value) => {
                                      if (edu.level === "Other") {
                                        handleEducationChange(
                                          index,
                                          "other_level_text",
                                          value
                                        );
                                      } else {
                                        handleEducationChange(
                                          index,
                                          "level",
                                          value
                                        );
                                      }
                                    }}
                                  />
                                  <EditableField
                                    label="Institution"
                                    value={edu.institution_name}
                                    isEditing={isEditing}
                                    onChange={(value) =>
                                      handleEducationChange(
                                        index,
                                        "institution_name",
                                        value
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="Year of Passing"
                                    value={edu.year_of_passing}
                                    isEditing={isEditing}
                                    type="number"
                                    onChange={(value) =>
                                      handleEducationChange(
                                        index,
                                        "year_of_passing",
                                        parseInt(value)
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="Percentage"
                                    value={edu.percentage_marks}
                                    isEditing={isEditing}
                                    type="number"
                                    onChange={(value) =>
                                      handleEducationChange(
                                        index,
                                        "percentage_marks",
                                        parseFloat(value)
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </InfoSection>
                      )}

                    {/* ✅ Hide Work Experience if Not Registered */}
                    {employee.registrationComplete &&
                      fullEmployeeData.work_experience &&
                      fullEmployeeData.work_experience.length > 0 && (
                        <InfoSection
                          title="Work Experience"
                          icon={<CurrencyDollarIcon className="w-5 h-5" />}
                        >
                          <div className="space-y-4">
                            {editedData.work_experience.map((work, index) => (
                              <div
                                key={work.id || index}
                                className="p-4 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg border border-emerald-200 hover:border-emerald-300 transition-colors duration-200"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  <EditableField
                                    label="Company"
                                    value={work.company_name}
                                    isEditing={isEditing}
                                    onChange={(value) =>
                                      handleWorkExperienceChange(
                                        index,
                                        "company_name",
                                        value
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="Role"
                                    value={work.role}
                                    isEditing={isEditing}
                                    onChange={(value) =>
                                      handleWorkExperienceChange(
                                        index,
                                        "role",
                                        value
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="Start Date"
                                    value={work.start_date}
                                    isEditing={isEditing}
                                    type="date"
                                    onChange={(value) =>
                                      handleWorkExperienceChange(
                                        index,
                                        "start_date",
                                        value
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="End Date"
                                    value={work.end_date || "Present"}
                                    isEditing={isEditing}
                                    type="date"
                                    onChange={(value) =>
                                      handleWorkExperienceChange(
                                        index,
                                        "end_date",
                                        value
                                      )
                                    }
                                  />
                                  <EditableField
                                    label="Last Salary"
                                    value={work.last_salary}
                                    isEditing={isEditing}
                                    type="number"
                                    onChange={(value) =>
                                      handleWorkExperienceChange(
                                        index,
                                        "last_salary",
                                        parseFloat(value)
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </InfoSection>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-600">
                      Unable to load employee details
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-8 py-6 bg-gradient-to-r from-emerald-50 to-green-50 border-t border-emerald-100 flex justify-between items-center">
                <div className="flex space-x-3">
                  <button
                    onClick={async (e) => {
                      try {
                        const button = e.currentTarget;
                        const originalText = button.innerHTML;

                        // Disable button and show loading state
                        button.disabled = true;
                        button.innerHTML = `
                          <svg class="animate-spin h-5 w-5 mr-2 inline" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                          Downloading...
                        `;

                        // Call the download service
                        await DownloadAllfilesService(employee.employeeId);

                        // Show success state
                        button.innerHTML = `
                          <svg class="h-5 w-5 mr-2 inline text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                          </svg>
                          Downloaded!
                        `;

                        // Reset button after 2 seconds
                        setTimeout(() => {
                          button.disabled = false;
                          button.innerHTML = originalText;
                        }, 2000);
                      } catch (error) {
                        console.error("Error downloading files:", error);
                        ("Failed to download files. Please try again.");
                        e.currentTarget.disabled = false;
                        e.currentTarget.innerHTML = `
                          <svg class="h-5 w-5 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                          </svg>
                          Download Documents
                        `;
                      }
                    }}
                    className="flex items-center px-5 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-xl border border-gray-300 hover:border-emerald-300 transition-all duration-200 hover:shadow-sm group relative disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5 mr-2 group-hover:text-emerald-600" />
                    Download Documents
                  </button>

                  <button
                    onClick={async (e) => {
                      try {
                        const button = e.currentTarget;
                        const originalText = button.textContent;
                        button.disabled = true;
                        button.textContent = "Downloading...";

                        await AdminDownloadProfilePdfService(
                          employee.employeeId,
                        );

                        button.textContent = "Downloaded!";
                        setTimeout(() => {
                          button.disabled = false;
                          button.textContent = originalText;
                        }, 2000);
                      } catch (err) {
                        console.error("Error downloading PDF:", err);
                        ("Failed to download PDF");
                        e.currentTarget.disabled = false;
                      }
                    }}
                    className="flex items-center px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-xl border border-transparent transition-all duration-200 hover:shadow-lg group relative overflow-hidden"
                  >
                    {/* Button Glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>

                    {/* Border Animation */}
                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-emerald-300 rounded-xl transition-all duration-300"></div>

                    <ArrowDownTrayIcon className="w-5 h-5 mr-2 relative z-10" />
                    <span className="relative z-10">Download PDF</span>
                  </button>
                </div>

                {isEditing ? (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setEditedData(fullEmployeeData);
                        setIsEditing(false);
                      }}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-900 hover:text-white rounded-xl border border-gray-300 hover:border-gray-900 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdits}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-xl transition-all duration-200 hover:shadow-lg relative overflow-hidden group"
                    >
                      {/* Button Glow */}
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>

                      {/* Border Animation */}
                      <div className="absolute inset-0 border-2 border-transparent group-hover:border-emerald-300 rounded-xl transition-all duration-300"></div>

                      <span className="relative z-10">Save Changes</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={closeModal}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-black-50 rounded-xl border border-gray-300 hover:border-emerald-300 transition-colors duration-200"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper Components
const InfoSection = ({ title, icon, children }) => (
  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200 hover:border-emerald-300 transition-colors duration-200">
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
      <span className="text-emerald-600 mr-2">{icon}</span>
      {title}
    </h3>
    {children}
  </div>
);

const InfoGrid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const InfoItem = ({ label, value, mono = false, fullWidth = false }) => (
  <div className={fullWidth ? "md:col-span-2" : ""}>
    <span className="text-xs text-gray-500 block mb-1">{label}</span>
    <p
      className={`font-medium text-gray-900 ${mono ? "font-mono" : ""} ${
        fullWidth ? "" : "truncate"
      }`}
    >
      {value || "N/A"}
    </p>
  </div>
);

const EditableField = ({
  label,
  value,
  isEditing,
  onChange,
  mono = false,
  fullWidth = false,
  type = "text",
}) => (
  <div className={fullWidth ? "md:col-span-2" : ""}>
    <span className="text-xs text-gray-500 block mb-1">{label}</span>
    {isEditing ? (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 transition-colors duration-200 ${
          mono ? "font-mono" : ""
        }`}
      />
    ) : (
      <p
        className={`font-medium text-gray-900 ${mono ? "font-mono" : ""} ${
          fullWidth ? "" : "truncate"
        }`}
      >
        {value || "N/A"}
      </p>
    )}
  </div>
);

EmployeeCard.displayName = "EmployeeCard";

export default EmployeeCard;
